import { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PageContext from './PageContext';
import PageReadOnlyInput from '../sub-components/PageReadOnlyInput';
import OrderedDataTable from './OrderedDataTableCopy';
import WorkflowAgreggateRow from './WorkflowAggregateRow';
import WorkflowOptOut from './WorkflowOptOut';
import WorkflowCommentLog from './WorkflowCommentLogCopy.jsx';
import PageDropdown from './PageDropdownCopy.jsx';
import { HttpContext } from '../../api-services/HttpService';
import ApiRequestButton from './ApiRequestButton';
import CCOffcanvas from './CCOffcanvas.tsx';
import EllipsisInfoLabel from './EllipsisInfoLabel.jsx';
import BannerAlert from './BannerAlertCopy.jsx';
import isNullOrUndefined from '../functions/isNullOrUndefined.js'

const WorkflowModal = () => { 
    // initialize PageContext 
    const {
        workflowModal, 
        setWorkflowModal, 
        workflowAction, 
        workflowNewStatus, 
        setWorkflowNewStatus, 
        workflowAggregateTableHeader, 
        workflowAggregateDataList, 
        workflowButtonCss, 
        workflowOptOut, 
        workflowReviewAction, 
        status, 
        fullRefresh,
        snackbarAlert, 
        workflowActionId, 
        rebateInputTypeId
    } = useContext(PageContext);
    // initialize http context 
    const {
        PUT, 
    } = useContext(HttpContext);
    // initialize state
    const [submissionComment, setSubmissionComment ] = useState('');
    const [reviewType, setReviewType] = useState('--select--');
    const [reviewTypeList, setReviewTypeList] = useState(['--select--', 'Verified', 'Expected Match']);
    // initialize state for opt out
    const [optOut, setOptOut] = useState(false);
    // global redux state for company (passed in from other route)
    const company = useSelector((state) => state.company);
    const companyInfo = useSelector((state) => state.companyInfo);

    // function wrapper to pass row component to datatable
    const RowComponent = (row, count, tableHeader,stickyEnabled) =>{
        return(
            <>
                <WorkflowAgreggateRow row={row} count={count} tableHeader={tableHeader} rateTypeId={rebateInputTypeId} stickyEnabled={stickyEnabled}/>
            </>
        );
    }  

    // handler for when action button is clicked
    const onAction = () => {
        return new Promise((resolve, reject) => {
            let response; 
            // validate comment
            if (submissionComment.length == 0){
                snackbarAlert(true, 'Error: Please input comment.', 'error');
                reject(); 
                return;
            }
            // if submitting to initial review, ensure there is a CCRM assigned to master company
            if (workflowNewStatus == 'Initial Review' && !companyInfo['primaryccrm']){
                snackbarAlert(true, 'Error: Cannot submit to Initial Review when no ' + process.env.REACT_APP_CCRM_DESCRIPTION + ' assigned.', 'error'); 
                reject(); 
                return; 
            }   
            // specify arguments based on current status
            let args = {
                workflowactionid: workflowActionId,  
                workflowid: companyInfo['workflowid'], 
                mastercompanyid: companyInfo['mastercompanyid'],
                status: workflowNewStatus, 
                comment: submissionComment, 
                oldstatus: status, 
            }
            // make put request for submitting a pending workflow
            PUT('/workflow/companyPayoutViewPage', args).then(res => {
                console.log(res);
                response = res; 
                // hide workflow modal
                setWorkflowModal(workflowModal => workflowModal = false);
                // reset comment
                setSubmissionComment(submissionComment => submissionComment = '');
                // reset opt out
                setOptOut(optOut => optOut = false);
                // show snackbar on success
                snackbarAlert(true, 'Successfully updated workflow.', 'success');
                // refresh data on screen
                return fullRefresh();
            }).then(res => {
                console.log(res); 
                // resolve on success
                resolve(res); 
            }).catch(err => {
                console.log(err);
                // show snackar on error
                snackbarAlert(true, 'Error updating workflow.', 'error');
                // reject on error
                reject(err); 
            });
        }); 
    }

    // useEffect for opt out
    useEffect(() => {
        if (workflowAction == 'Submit'){
            if (optOut){
                // if opt out is true new status is final review to skip initial review
                setWorkflowNewStatus(workflowNewStatus => workflowNewStatus = 'Final Review');
            }
            else{
                // if op out is false new status is Initial Review to have initial review then final review
                setWorkflowNewStatus(workflowNewStatus => workflowNewStatus = 'Initial Review');
            }
        }
        else {
            setOptOut(optOut => optOut = false);
        }
    }, [optOut]);

    // useEffect to mark as opt out if no ccrm
    useEffect(() => {
        if (workflowAction == 'Submit' && !companyInfo['primaryccrm']){
            setOptOut(optOut => optOut = true);
        }
        else {
            setOptOut(optOut => optOut = false);
        }
    }, [workflowModal]); 

    return(
        <>  
            <CCOffcanvas
                title={company + " Workflow Form"}
                show={workflowModal}
                onCanvasClose={()=>setWorkflowModal(workflowModal => workflowModal = false)}
                bodyCssClassName="offcanvas-body"
                offCanvasClassName="offcanvas-parent-card"
                >
                    <div className="d-flex flex-column h-100">
                        <div className="bg-white p-3" style={{ fontSize: "14px" }}>   
                            <div className="d-flex gap-3">
                                <EllipsisInfoLabel title={'Company:'} content={company} maxChar='50'/>
                                <div className="vr"></div>
                                {isNullOrUndefined(companyInfo['incentivefreq']) || companyInfo['incentivefreq'].length ?  
                                <EllipsisInfoLabel title={'Payout Cycle:'} content={companyInfo['cyclestart'] + ' to ' + companyInfo['cycleend'] } maxChar='30'/>
                                : <EllipsisInfoLabel title={'Payout Cycle:'} content={companyInfo['cyclestart'] + ' to ' + companyInfo['cycleend'] + ' [' + companyInfo['incentivefreq'] + ']'} maxChar='30'/>}
                                
                            </div>
                        </div>
                        <div className="d-flex flex-column flex-grow-1 overflow-scroll">
                            <div className="p-3 trail-section">
                                <div className="bg-white trail-card">
                                    <OrderedDataTable RowComponent={RowComponent} tableHeader={workflowAggregateTableHeader} dataList={workflowAggregateDataList} sortingEnabled={true} stickyFirstColumnEnabled={true} disableMaxTableHeight={true}/>
                                </div>
                            </div>
                            {/* show comment log */}
                            <div className="p-3 comment-section">
                                <div className="bg-white comment-card">
                                    <WorkflowCommentLog workflowid={companyInfo['workflowid']} disableHeight={true}/>
                                    {/* textarea input for new comment */}
                                    <div className="p-3" style={{borderTop: "1px solid #ECECEC"}}>
                                        <input class='w-100 input-text-field' value={submissionComment} onChange={(e)=>setSubmissionComment(submissionComment => submissionComment = e.target.value)} placeholder='Type comments here'></input>
                                    </div>
                                </div>
                            </div>
                            {/* show dropdown when flag is true */}
                            {workflowReviewAction.current ?
                            <>
                                <div className="p-3 trail-section">
                                    <div className="bg-white trail-card d-flex align-items-center justify-content-start p-2">
                                        <PageDropdown tag='Review Type: ' value={reviewType} setValue={setReviewType} list={reviewTypeList}/>
                                    </div>
                                </div>
                            </>
                            :<></>}
                            {/* show opt out option when flag is true */}
                            {workflowOptOut.current ?
                            <div className="p-3 comment-section">
                                <div className="bg-white comment-card">
                                    <div className='p-3'>  
                                        <WorkflowOptOut value={optOut} setValue={setOptOut} />  
                                        <BannerAlert disableClose show={true} type='info' message={'Company without a ' + process.env.REACT_APP_CCRM_DESCRIPTION + ' will automatically be opted out of ' + process.env.REACT_APP_CCRM_DESCRIPTION + ' review.'} width='100%' /> 
                                    </div>
                                </div>
                            </div>                
                            :<></>}
                            {/* show review submissionComments when flag is true */}
                        </div>
                        <div
                            className="d-flex justify-content-end p-3 w-100 offcanvas-footer z-2" style={{height: '65px'}}
                        >
                            {/* <button class={workflowButtonCss.current} onClick={()=>onAction()}>{workflowAction}</button> */}
                            <ApiRequestButton onButtonClick={onAction} tag={workflowAction}/>
                        </div>
                    </div>
            </CCOffcanvas>
        </>
    );
}

export default WorkflowModal; 